import { getWebServiceParamName, getWebServiceValueFn } from '../conf/conf';
import appStore from '../redux/store/appStore';
import { markersToUrlValue } from '../helpers/google-map-helper';
import { getItem } from '../helpers/object-helper';

/**
 * Returns the http request data for http-client.quotationsGet(body) request
 * @return {Object}
 *  .indirizzo {String}
 *      Sample
 *          "Via Enrico Gonzales, 4, Milano, MI, Italia"
 *  .pv {String}
 *      Sample
 *          "MI"
 *  .email {String}
 *  .systemCoords {String}
 *      Sample
 *          "45.4444971,9.2200075"
 *  .powerFTV {String}
 *      Sample
 *          "=10"
 *  .fabbisogno {Float}
 *      Sample
 *          137.5
 *  .utilizzo {Integer}
 *      Sample
 *          3
 *  .center {String}
 *      Sample
 *          "45.444497,9.220008"
 *  .zoom {Integer}
 *      Sample
 *          18
 *  .markers {String}
 *      Sample
 *          "markers=color:%7Clabel:%7C45.4444971,9.2200075&"
 */
const quotationsGetRequestData = () => {

    const requestData = {};

    // fetch application state to populate data
    const appState = appStore.getState();

    // map data
    let mCenter = null;
    let mZoom = null;
    let mMarkers = null;

    const map = getItem('map.refs.map', appState);
    if (map) {
        const polygon = getItem('map.refs.polygon', appState);
        const polygonPath = (polygon) ? polygon.getPath() : null;
        const mapCenter = map.getCenter();
        mCenter = (polygon) ? polygon.getCenter().toUrlValue() : mapCenter.toUrlValue() ;
        mZoom = map.getZoom();
        const marker = getItem('map.refs.marker', appStore.getState());
        mMarkers = markersToUrlValue([marker]);
    }
    
    requestData['center'] = mCenter;
    requestData['zoom'] = mZoom;
    requestData['markers'] = mMarkers;

    // configuration data
    const items = [
        'user.address',
        'user.pv',
        'user.email',
        'configuration.systemCoords',
        'configuration.powerFTV',
        'configuration.electricityConsumption',
        'configuration.houseUsage'
    ];
    for(let item of items) {
        const key = webServiceParamName(item);
        const value = webServiceParamValue(item, getItem(item, appState));
        requestData[key] = value;
    }

    return requestData;

};

/**
 * Returns the http request data for http-client.quotationsGenerate(body) request
 * @return {Object}
 *  .uuid {String} required
 *  .nome {String} required
 *  .cognome {String} required
 *  .ragionesociale {String} required
 *  .cfpiva {String}
 *      [null|''|'<CODICE_FISCALE_VALIDO>'|'<PARTITA_IVA_VALIDA>']
 *  .indirizzo {String} required
 *  .cap {String} required
 *  .citta {String} required
 *  .telefono {String} required
 *  .tipoImmobile {String} required
 *  .email {String} required
 *  .impianto {String} required
 *      ['basic'|'basic-accumulo'|'plus'|'plus-accumulo'|'plus-s'|'plus-s-accumulo']
 *  .electricityConsumptionPeople {String} required
 *  .electricityConsumptionCost {Number} required
 *  .consensoReadPrivacy {Boolean}
 *  .consensoAuthorizeA {Boolean} 
 *  .consensoAuthorizeB {Boolean}
 *  .consensoAuthorizeC {Boolean}
 *  .consensoAuthorizeD {Boolean}
 *  .channel {String}
 *  .solarSpecialist {String}
 *  .campaign {String}
 */
const quotationGenerateRequestData = () => {

    const requestData = {};

    // fetch application state to populate data
    const appState = appStore.getState();

    // populate data
    requestData.uuid = getItem('quotations.uuid', appState);
    requestData.nome = getItem('quotationsForm.nome', appState);
    requestData.cognome = getItem('quotationsForm.cognome', appState);
    requestData.ragionesociale = getItem('quotationsForm.ragionesociale', appState);
    requestData.cfpiva = getItem('quotationsForm.cfpiva', appState);
    requestData.indirizzo = getItem('user.address', appState);
    requestData.cap = getItem('user.postalCode', appState);
    requestData.citta = getItem('user.city', appState);
    requestData.telefono = getItem('quotationsForm.telefono', appState);
    requestData.tipoImmobile = getItem('quotationsForm.tipoImmobile', appState);
    requestData.email = getItem('quotationsForm.email', appState);
    requestData.impianto = getItem('quotations.selectedImpianto', appState);
    requestData.electricityConsumptionPeople = getItem('configuration.electricityConsumptionPersone', appState);
    requestData.electricityConsumptionCost = getItem('configuration.electricityConsumptionCost', appState);
    requestData.consensoReadPrivacy = getItem('quotationsForm.consensoReadPrivacy', appState);
    requestData.consensoAuthorizeA = getItem('quotationsForm.consensoAuthorizeA', appState);
    requestData.consensoAuthorizeB = getItem('quotationsForm.consensoAuthorizeB', appState);
    requestData.consensoAuthorizeC = getItem('quotationsForm.consensoAuthorizeC', appState);
    requestData.consensoAuthorizeD = getItem('quotationsForm.consensoAuthorizeD', appState);
    requestData.channel = getItem('applicationContext.ch', appState);

    const solarSpecialist = getItem('applicationContext.ss', appState);
    if (solarSpecialist) {
        requestData.solarSpecialist = solarSpecialist;
    }

    const campaign = getItem('applicationContext.cmp', appState);
    if (campaign) {
        requestData.campaign = campaign;
    }

    // return
    return requestData;
};

/**
 * Returns the name for itemPath item when it is passed as a web service parameter
 * Result is driven by configuration (/conf/conf.js)
 * @param {String} itemPath
 *  Sample
 *      'configuration.roofArea'
 * @return {String}
 *  Sample
 *      'area'
 */
const webServiceParamName = (itemPath) => {
    const webServiceParamName = getWebServiceParamName(itemPath);
    if (webServiceParamName) {
        return webServiceParamName;
    } else {
        const itemPathElements = itemPath.split('.');
        return itemPathElements[itemPathElements.length - 1];
    }
};

/**
 * Returns the value for itemPath item when it is passed as a web service parameter
 * Result is driven by configuration (/conf/conf.js)
 * @param {String} itemPath
 *  Sample
 *      'configuration.roofTipology'
 * @param {Mixed} value
 *  Sample
 *      '1falda'
 * @return {Mixed}
 *  Sample
 *      1
 */
const webServiceParamValue = (itemPath, value) => {
    const webServiceValueFn = getWebServiceValueFn(itemPath);
    return (webServiceValueFn) ? webServiceValueFn(value) : value;
};

export {
    quotationsGetRequestData,
    quotationGenerateRequestData
}